import { TimeString, UsDateString, usToIso } from './date';
import { timeForInstant, toInstant, usDateForInstant } from './emergencyShift';

/**
 * A 24/7 booking runs as back-to-back 12-hour shifts from the time the user
 * picks. Blocks match the `customShiftProps` rows `handleSaveShift` builds, so
 * they push straight into its pipeline. `endTime < startTime` marks a block
 * that ends the following day.
 */
export interface Rotating247Block {
    startTime: TimeString;
    /** The day the block itself starts on, "MM-DD-YYYY" */
    date: UsDateString;
    endTime: TimeString;
    /** The selected day the pair belongs to, "MM-DD-YYYY" */
    blockDate: UsDateString;
}

const MINUTES_PER_HALF_DAY = 12 * 60;

/**
 * The two shifts covering one selected day of a 24/7 booking: the picked start
 * to 12 hours later, then that to 12 hours later again. Every selected day is
 * built on its own, so days that are not next to each other behave the same.
 *
 * Both blocks carry the selected day as their `blockDate`, which is how the
 * form keeps a day's pair together when a different day is removed. The night
 * block's own `date` is the day it starts on, which is the next day once the
 * picked time is 12:00 or later.
 *
 * Returns [] when the date or time cannot be read.
 *
 * @param  date      The selected day, "MM-DD-YYYY"
 * @param  startTime When the first shift starts, "HH:mm" or "HH:mm:ss"
 * @return the day block then the night block
 */
export const buildRotating247Blocks = (
    date: UsDateString,
    startTime: TimeString,
): Rotating247Block[] => {
    const start = toInstant(usToIso(date), startTime);

    if (start === null) {
        return [];
    }

    const middle = start + MINUTES_PER_HALF_DAY;
    const end = middle + MINUTES_PER_HALF_DAY;

    return [
        {
            startTime: timeForInstant(start),
            endTime: timeForInstant(middle),
            date,
            blockDate: date,
        },
        {
            startTime: timeForInstant(middle),
            endTime: timeForInstant(end),
            date: usDateForInstant(middle),
            blockDate: date,
        },
    ];
};
